import { Empty } from 'antd';
import clsx from 'clsx';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Flex, Gap, Text, TimeWithTimeZone } from '@common/components';
import { DetailItemAttachmentType } from '@onboarding/graphql';
import { AttachmentSelector } from './_file-viewer-attachment-selector';
import { FilePreview } from './_file-viewer-file-preview';

export function AttachmentViewer({
  children,
  className,
  source,
  onEdit,
}: PropsWithChildren<{
  className?: string;
  source: DetailItemAttachmentType[];
  onEdit?: () => void;
}>) {
  const hasAnySource = source.some((a) => a?.file.downloadUrl);
  const [selectedAttachment, setSelectedAttachment] = useState<
    DetailItemAttachmentType | undefined
  >(source[0]);

  const setSelected = useCallback(
    (fileId: string) => {
      const attachment = source.find((sourceFile) => sourceFile.id === fileId);

      setSelectedAttachment(attachment);
    },
    [source]
  );

  useEffect(() => {
    setSelected(source[0]?.id);
  }, [setSelected, source]);

  return (
    <Gap className={clsx([className, 'wk-attachment-viewer'])} fullHeight>
      {hasAnySource ? (
        <Gap size="md" vertical fullHeight fullWidth>
          {children && <div>{children}</div>}

          <Gap vertical size="xs" fullWidth>
            <AttachmentSelector
              sourceAttachments={source}
              selectedAttachment={selectedAttachment}
              onChange={(fileId: string) => {
                setSelected(fileId);
              }}
              onEdit={onEdit}
            />

            {selectedAttachment?.statusChangedAt && (
              <Text size="sm" color="midnightRegular">
                <Text size="sm" color="midnightRegular" transform="capitalize">
                  {selectedAttachment.status}
                </Text>{' '}
                on{' '}
                <TimeWithTimeZone
                  date={selectedAttachment?.statusChangedAt}
                  variant="long_format"
                />
              </Text>
            )}
          </Gap>

          {selectedAttachment?.file.downloadUrl ? (
            <Flex fullWidth fullHeight>
              <FilePreview
                fileName={selectedAttachment.file.filename}
                assetUrl={selectedAttachment?.file.downloadUrl}
                contentType={selectedAttachment?.file.contentType}
              />
            </Flex>
          ) : (
            <Empty data-testid="empty-state" description="No file found" />
          )}
        </Gap>
      ) : (
        <Empty data-testid="empty-state" />
      )}
    </Gap>
  );
}
